import { useQuery } from '@tanstack/react-query';

import { PERSONAL_CHECKS_API } from '../../constants';
import { CheckrPersonalUser } from './types';

class FetchError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// Supplying a retry function replaces react-query's default 3-attempt cap, so
// anything this keeps answering `true` to retries forever and the query never
// settles — it stays pending with `data` undefined, which the banner reads as an
// empty profile. Cross-origin rejections and unparseable bodies both land here.
const MAX_RETRIES = 3;

export function useCheckrPersonalUserQuery() {
  const query = useQuery<CheckrPersonalUser | null>({
    queryKey: ['personal/user', 'me'],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const url = new URL('/user/me', PERSONAL_CHECKS_API);
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new FetchError('Failed to fetch user data', response.status);
      }

      try {
        return (await response.json()) as CheckrPersonalUser;
      } catch {
        throw new FetchError(
          'Personal user response body was not JSON',
          response.status,
        );
      }
    },
    retry(failureCount, error) {
      if (failureCount >= MAX_RETRIES) {
        return false;
      }
      // A 4xx and a 200 carrying a non-JSON body both answer the same way every
      // time. Only server errors are worth another attempt.
      if (error instanceof FetchError) {
        return error.status >= 500;
      }
      // Network and CORS failures arrive as TypeError and can be transient.
      return true;
    },
  });

  return query;
}
